import React, { useState } from 'react';
import { Comment } from '../../types/typing.ts';
import { getAvatarColor } from './avatarColors.ts';
import './commentsmenu.css';

interface CommentsProps {
  isOpen: boolean;
  onClose: () => void;
  comments: Comment[];
  onAddComment: (text: string, parentId: string | null) => void;
  selectedCommentId: string | null;
}

const Comments: React.FC<CommentsProps> = ({
  isOpen,
  onClose,
  comments,
  onAddComment,
  selectedCommentId,
}) => {
  const [newComment, setNewComment] = useState('');
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [visibleReplies, setVisibleReplies] = useState<{
    [key: string]: boolean;
  }>({});

  const toggleReplies = (commentId: string) => {
    setVisibleReplies((prev) => ({
      ...prev,
      [commentId]: !prev[commentId],
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (newComment.trim()) {
      onAddComment(newComment, replyingTo);
      setNewComment('');
    }
  };

  const formatTimestamp = (seconds: number) => {
    const ms = Math.floor((seconds % 1) * 100);
    const totalSeconds = Math.floor(seconds);
    const hrs = Math.floor(totalSeconds / 3600);
    const mins = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;

    return `${String(hrs).padStart(2, '0')}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}.${String(ms).padStart(2, '0')}`;
  };

  if (!isOpen) return null;

  return (
    <div className="comments-overlay">
      <div className="comments-container">
        <div className="comments-header">
          <span>Comments</span>
          <button className="close-button" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div className="comments-list">
          {comments.map((comment) => (
            <div
              key={comment.id}
              id={`comment-${comment.id}`}
              className={`comment ${selectedCommentId === comment.id ? 'pop' : ''}`}
            >
              <div
                className="avatar"
                style={{
                  backgroundColor:
                    comment.author.color ||
                    getAvatarColor(comment.author.name),
                }}
              >
                {comment.author.initials}
              </div>
              <div className="comment-content">
                <div className="comment-header">
                  <span className="author-name">{comment.author.name}</span>
                </div>
                <div className="comment-body">
                  <span className="comment-timestamp">
                    {formatTimestamp(comment.timestamp)}
                  </span>
                  <span className="comment-text">{comment.text}</span>
                </div>

                {/* Reply & Show Replies Buttons */}
                <div className="comment-actions">
                  {/* Reply or Show Replies Button */}
                  {comment.replies && comment.replies.length > 0 ? (
                    <button
                      className="reply-button"
                      onClick={() => {
                        toggleReplies(comment.id);
                        if (replyingTo) setReplyingTo(null);
                        else setReplyingTo(comment.id);
                      }}
                    >
                      {visibleReplies[comment.id]
                        ? 'Hide Replies'
                        : `Show Replies (${comment.replies.length})`}
                    </button>
                  ) : (
                    <button
                      className="reply-button"
                      onClick={() => {
                        if (replyingTo) setReplyingTo(null);
                        else setReplyingTo(comment.id);
                      }}
                    >
                      Reply
                    </button>
                  )}
                </div>

                {/* Replies Section */}
                {/* Display Replies */}
                {visibleReplies[comment.id] && (
                  <div className="replies">
                    {comment.replies?.map((reply) => (
                      <div key={reply.id} className="reply">
                        <div
                          className="avatar"
                          style={{
                            backgroundColor:
                              reply.author.color ||
                              getAvatarColor(reply.author.name),
                          }}
                        >
                          {reply.author.initials}
                        </div>
                        <div className="reply-content">
                          <span className="author-name">
                            {reply.author.name}
                          </span>
                          <span className="comment-text">{reply.text}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Reply Input Box (Independent from Reply Display) */}
                {replyingTo === comment.id && (
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      // Update visibility for this comment's replies
                      setVisibleReplies((prev) => ({
                        ...prev,
                        [comment.id]: true,
                      }));
                      // Call your existing handleSubmit logic
                      handleSubmit(e);
                    }}
                    className="reply-input-container"
                  >
                    <input
                      type="text"
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      placeholder="Write a reply..."
                      className="comment-input"
                    />
                    <button type="submit" className="submit-reply">
                      Send
                    </button>
                  </form>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* New Comment Input */}
        {!replyingTo && (
          <form onSubmit={handleSubmit} className="comment-input-container">
            <input
              type="text"
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="Enter Comment here..."
              className="comment-input"
            />
          </form>
        )}
      </div>
    </div>
  );
};

export default Comments;
